import { Button, DialogTitle, Text } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";

import { TableFieldsService } from "@/client";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog";
import useCustomToast from "@/hooks/useCustomToast";

const PER_PAGE = 5;

interface DeleteTableFieldProps {
  id: string;
  currentPage: number;
  totalCount: number;
  onPageChange?: (page: number) => void;
}

const DeleteTableField = ({
  id,
  currentPage,
  totalCount,
  onPageChange,
}: DeleteTableFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const deleteTableField = async (id: string) => {
    await TableFieldsService.deleteTableField({ id: id });
  };

  const mutation = useMutation({
    mutationFn: deleteTableField,
    onSuccess: () => {
      showSuccessToast("테이블 필드가 성공적으로 삭제되었습니다.");
      setIsOpen(false);

      // 페이지네이션 처리: 현재 페이지의 마지막 아이템을 삭제한 경우 이전 페이지로 이동
      const itemsInCurrentPage = totalCount % PER_PAGE || PER_PAGE;
      const isLastItemInPage = itemsInCurrentPage === 1;
      const isNotFirstPage = currentPage > 1;

      if (isLastItemInPage && isNotFirstPage && onPageChange) {
        onPageChange(currentPage - 1);
      }
    },
    onError: () => {
      showErrorToast("테이블 필드 삭제 중 오류가 발생했습니다.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "tableFields",
      });
    },
  });

  const onSubmit = async () => {
    mutation.mutate(id);
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" colorPalette="red">
          <FiTrash2 fontSize="16px" />
          테이블 필드 삭제
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>테이블 필드 삭제</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>
              이 테이블 필드는 영구적으로 삭제됩니다. 계속하시겠습니까? 이
              작업은 되돌릴 수 없습니다.
            </Text>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              colorPalette="red"
              type="submit"
              loading={isSubmitting}
            >
              삭제
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteTableField;
